import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  EyeOff,
  Lock,
  Search,
  Trash2,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { DosPaneles } from "@/components/documentos/dos-paneles";
import {
  BotonDelPapel,
  PapelDelExtracto,
} from "@/components/documentos/papel-del-extracto";
import { EmptyState } from "@/components/feedback/empty-state";
import { type Papel, Visor } from "@/components/propuesta/visor";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/extracto/$archivoId")({
  component: FichaDelExtracto,
});

// ===========================================================================
// Un estado de cuenta cargado, con todas sus filas.
//
// Es el paso 4 del recorrido, con las palabras del área: «ver la pantalla de
// este estado de cuenta que se ha cargado, todas las filas una por una, como
// si fuera una tabla, y también poder editarlo después».
//
// LO QUE SE PUEDE EDITAR Y LO QUE NO. Un movimiento cruzado contra una boleta
// lleva un importe aplicado y la firma de quien lo decidió. Cambiarle la cifra
// por detrás deja ese cruce apuntando a algo que ya no existe, y el descuadre
// aparece semanas después sin rastro de dónde salió. Esas filas salen con
// candado y un enlace a su cruce; el resto se escribe directo en la celda.
// ===========================================================================

const POR_PAGINA = 200;

const SIMBOLO = { PEN: "S/", USD: "US$" } as const;

const dinero = (c: number | null) =>
  c === null
    ? "—"
    : (c / 100).toLocaleString("es-PE", { minimumFractionDigits: 2 });

/** `1,234.50` → 123450. Vacío es `null`, que no es lo mismo que cero. */
function aCentimos(texto: string): number | null {
  const limpio = texto
    .trim()
    .replace(/[^\d.,-]/g, "")
    .replace(/,/g, "");
  if (!limpio) return null;
  const n = Number.parseFloat(limpio);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}

/**
 * Cómo se LEE una cifra en la celda: con separador de miles, como el resto.
 *
 * `toFixed(2)` dejaba «7500.00» en Entró y Salió mientras «Saldo calc.» —que
 * pasa por `dinero`— enseñaba «7,500.00» dos columnas más allá. La misma cifra
 * escrita de dos formas en la misma fila obliga a contar dígitos para saber si
 * son siete mil o setenta mil, que es justo lo que el separador evita.
 */
const aTexto = (centimos: number | null) =>
  centimos === null ? "" : dinero(centimos);

/**
 * Cómo se ESCRIBE: sin separadores, que estorban al mover el cursor.
 *
 * Se usa al entrar en la celda. `aCentimos` se come las comas igual, así que
 * corregir sobre «7,500.00» tampoco rompería nada — pero teclear entre comas
 * que aparecen y desaparecen solas es incómodo, y esta tabla se usa para
 * corregir a mano lo que el lector leyó mal.
 */
const aTextoLlano = (centimos: number | null) =>
  centimos === null ? "" : (centimos / 100).toFixed(2);

type Movimiento = {
  id: string;
  pagina: number | null;
  linea: number | null;
  orden: number;
  fechaOper: string;
  fechaValor: string | null;
  descripcion: string | null;
  referencia: string | null;
  nroOperacion: string | null;
  abono: number | null;
  cargo: number | null;
  /** El impuesto que el banco cobra en la MISMA línea. Resta como el cargo. */
  itf: number | null;
  saldo: number | null;
  /** Si entra en la conciliación. `false` la deja fuera a propósito. */
  conciliable: boolean;
  conciliado: boolean;
  cruceId: string | null;
};

/** Las columnas que se pueden escribir, en el orden en que se ven. */
const COLUMNAS = [
  { clave: "fechaOper", titulo: "Fecha", ancho: "w-[104px]" },
  { clave: "descripcion", titulo: "Descripción", ancho: "" },
  { clave: "nroOperacion", titulo: "N.º operación", ancho: "w-[116px]" },
  { clave: "abono", titulo: "Entró", ancho: "w-[110px]", dinero: true },
  { clave: "cargo", titulo: "Salió", ancho: "w-[110px]", dinero: true },
  // El BBVA cobra el impuesto en la misma línea y el saldo impreso ya lo lleva
  // descontado. En su columna, no como una fila repetida.
  { clave: "itf", titulo: "ITF", ancho: "w-[92px]", dinero: true },
  { clave: "saldo", titulo: "Saldo", ancho: "w-[120px]", dinero: true },
] as const;

type ClaveColumna = (typeof COLUMNAS)[number]["clave"];

/**
 * Lo que una casilla lleva escrito encima de lo que vino del servidor.
 *
 * `undefined` y `null` NO son lo mismo y confundirlos manda al servidor un
 * cambio que nadie pidió: `undefined` es «no lleva nada escrito, enseña lo del
 * servidor» y `null` es «esta línea no tiene cargo», que sí es una corrección.
 */
type Escrito = string | number | null | undefined;

/** Una tecla del atajo, dibujada como tecla. */
const Tecla = ({ children }: { children: React.ReactNode }) => (
  <kbd className="rounded border px-1 py-0.5 font-sans text-[10.5px]">
    {children}
  </kbd>
);

function FichaDelExtracto() {
  const { archivoId } = Route.useParams();
  const [pagina, setPagina] = useState(0);
  const [busca, setBusca] = useState("");
  const [aplicada, setAplicada] = useState("");
  const [soloConciliados, setSoloConciliados] = useState<
    "si" | "no" | undefined
  >(undefined);
  /** `no` deja solo las que están fuera de la conciliación. */
  const [verConciliables, setVerConciliables] = useState<
    "si" | "no" | undefined
  >(undefined);

  /**
   * El filtro por importe, que es el que faltaba.
   *
   * Buscar «300» en la descripción no encuentra un abono de 300 —el importe no
   * está en el texto— y en cambio saca todas las glosas que lleven ese número.
   * Pedido con la forma de la pantalla: «primero la casilla de mayor, menor o
   * igual, y a la derecha la casilla donde ingreso el número».
   *
   * `abono` por defecto porque es por lo que se pregunta casi siempre: lo que
   * hay que conciliar es lo que ENTRÓ.
   *
   * El monto va en unidades enteras y el servidor REDONDEA para compararlo, que
   * es lo que se pidió «para que no haya problemas decimales»: escribir 300
   * encuentra el de 300.00 y también el de 299.99.
   */
  const [campo, setCampo] = useState<"abono" | "cargo" | "saldo" | "itf">(
    "abono",
  );
  const [comparar, setComparar] = useState<"mayor" | "menor" | "igual">(
    "igual",
  );
  /** Tal como se escribe. Vacío = sin filtro de importe. */
  const [monto, setMonto] = useState("");
  const [montoAplicado, setMontoAplicado] = useState<number | undefined>(
    undefined,
  );

  /**
   * Las filas marcadas para actuar sobre ellas en bloque.
   *
   * Por id y no por posición: la página cambia y el índice deja de significar
   * lo mismo. Se conserva al pasar de página, que es lo que permite marcar en
   * la primera, seguir en la segunda y sacarlas todas de una vez.
   */
  const [marcadas, setMarcadas] = useState<Set<string>>(new Set());
  /** El importe por debajo del cual se marcan solas, tal como se escribe. */
  const [tope, setTope] = useState("10");
  /**
   * El importe A PARTIR DEL CUAL se marcan las salidas, tal como se escribe.
   *
   * Es el otro extremo del mismo problema. Lo de arriba saca el ruido de abajo
   * —el ITF de dos soles—; esto saca el de arriba: la transferencia de mil o de
   * diez mil que sale de la cuenta. Una salida no cruza nunca contra una boleta
   * de venta, que es un cobro, así que mientras cuente como pendiente el mes no
   * cierra y son las líneas más gordas del extracto las que lo trancan.
   */
  const [topeSalida, setTopeSalida] = useState("1000");
  /**
   * Por qué se marcaron las filas que hay marcadas. Va a la bitácora como
   * motivo de la exclusión.
   *
   * `null` es «elegidas a mano»: en cuanto se toca una casilla suelta deja de
   * ser cierto que salieron de un tope, y anotar un motivo que no fue es peor
   * que no anotar ninguno — dentro de tres meses alguien lo va a leer para
   * entender por qué esa línea de diez mil está fuera.
   */
  const [porQue, setPorQue] = useState<string | null>(null);
  const conciliables = api.marcarConciliables.useMutation();

  const { data, isPending } = api.extracto.useQuery(
    [
      "extracto",
      archivoId,
      String(pagina),
      aplicada,
      soloConciliados ?? "todos",
      verConciliables ?? "todas",
      `${campo}|${comparar}|${montoAplicado ?? ""}`,
    ],
    {
      params: { archivoId },
      query: {
        limite: POR_PAGINA,
        offset: pagina * POR_PAGINA,
        buscar: aplicada || undefined,
        conciliado: soloConciliados,
        conciliable: verConciliables,
        campo,
        comparar,
        monto: montoAplicado,
      },
    },
  );
  const d = data?.status === 200 ? data.body : null;
  const qc = useQueryClient();

  // Lo escrito encima de lo que vino del servidor. Se guarda por id y no por
  // posición: la página cambia y el índice deja de significar lo mismo.
  /** El papel abierto al lado. Se recuerda plegado: no todos lo quieren. */
  const [conPapel, setConPapel] = useState(true);
  /** El papel a pantalla completa, cuando la columna se queda corta. */
  const [papel, setPapel] = useState<Papel | null>(null);
  const eliminar = api.eliminarArchivo.useMutation();
  const navegar = useNavigate();

  const [tocado, setTocado] = useState<
    Record<string, Partial<Record<ClaveColumna, string | number | null>>>
  >({});
  const editar = api.editarMovimientos.useMutation();

  // Al cambiar de página o de filtro, lo escrito sin guardar deja de tener a
  // qué filas referirse. Se avisa antes en vez de perderlo en silencio.
  const sinGuardar = Object.keys(tocado).length;

  const filas: Movimiento[] = useMemo(() => {
    if (!d) return [];
    return (d.movimientos as Movimiento[]).map((m) => {
      const t = tocado[m.id];
      return t ? ({ ...m, ...t } as Movimiento) : m;
    });
  }, [d, tocado]);

  /**
   * Lo escrito antes de cada casilla, para poder deshacerlo con Ctrl+Z.
   *
   * Guarda el valor que HABÍA en `tocado` —`undefined` cuando no había nada
   * escrito encima y la casilla enseñaba lo del servidor—, no el del servidor:
   * deshacer tiene que devolver la casilla al escalón anterior, y con dos
   * correcciones seguidas sobre la misma línea esos dos escalones no son el
   * mismo.
   *
   * UN PASO POR CASILLA, no por tecla. Teclear «4500» son cuatro cambios, y un
   * Ctrl+Z que borrara un dígito cada vez no es lo que nadie espera de una
   * rejilla: se pide para deshacer LA CORRECCIÓN. Por eso solo se apunta el
   * primer cambio de una casilla; los siguientes, mientras se siga en la misma,
   * se pliegan sobre él.
   */
  type Paso = {
    id: string;
    clave: ClaveColumna;
    /** Lo que había escrito encima antes de la corrección. */
    antes: Escrito;
    /** Lo que dejó la corrección. Es a lo que vuelve el rehacer. */
    despues: Escrito;
    orden: number;
  };
  const [historial, setHistorial] = useState<Paso[]>([]);
  /**
   * Lo deshecho, para poder rehacerlo con Ctrl+Shift+Z.
   *
   * Se VACÍA en cuanto se escribe algo nuevo. Un rehacer guardado detrás de una
   * corrección posterior devolvería la casilla a un valor que ya no se sigue de
   * lo que hay en pantalla: sería inventar un paso que nadie dio.
   */
  const [deshecho, setDeshecho] = useState<Paso[]>([]);

  /**
   * Escribir un valor encima de una casilla, o quitarlo.
   *
   * `undefined` no es un valor: significa «esta casilla no lleva nada escrito
   * encima» y hay que BORRAR la clave, no poner `null`. `null` sí es un valor
   * —«esta línea no tiene cargo»— y dejarlo puesto mandaría al servidor un
   * cambio que nadie pidió. La fila entera sale de `tocado` si se queda sin
   * correcciones, que es lo que apaga su fondo ámbar y baja «sin guardar».
   */
  const escribir = (id: string, clave: ClaveColumna, valor: Escrito) => {
    setTocado((antes) => {
      const fila = { ...(antes[id] ?? {}) };
      if (valor === undefined) delete fila[clave];
      else fila[clave] = valor as never;
      const nuevo = { ...antes };
      if (Object.keys(fila).length === 0) delete nuevo[id];
      else nuevo[id] = fila;
      return nuevo;
    });
  };

  const cambiar = (m: Movimiento, clave: ClaveColumna, valor: string) => {
    if (m.conciliado) return;
    const v =
      clave === "abono" ||
      clave === "cargo" ||
      clave === "itf" ||
      clave === "saldo"
        ? aCentimos(valor)
        : valor === ""
          ? null
          : valor;
    setHistorial((antes) => {
      const ultimo = antes[antes.length - 1];
      // Mientras se siga en la misma casilla, el paso se estira en vez de
      // apilarse: los cuatro dígitos de «4500» son UNA corrección.
      if (ultimo && ultimo.id === m.id && ultimo.clave === clave)
        return [...antes.slice(0, -1), { ...ultimo, despues: v }];
      return [
        ...antes,
        {
          id: m.id,
          clave,
          antes: tocado[m.id]?.[clave],
          despues: v,
          orden: m.orden,
        },
      ];
    });
    setDeshecho([]);
    escribir(m.id, clave, v);
  };

  const nombreDe = (clave: ClaveColumna) =>
    COLUMNAS.find((c) => c.clave === clave)?.titulo;

  /** Deshacer la última casilla corregida. */
  const deshacer = () => {
    const paso = historial[historial.length - 1];
    if (!paso) return;
    setHistorial((h) => h.slice(0, -1));
    setDeshecho((r) => [...r, paso]);
    escribir(paso.id, paso.clave, paso.antes);
    toast.success(
      `Deshecho: ${nombreDe(paso.clave)} de la línea ${paso.orden}`,
    );
  };

  /** Rehacer la última que se deshizo. */
  const rehacer = () => {
    const paso = deshecho[deshecho.length - 1];
    if (!paso) return;
    setDeshecho((r) => r.slice(0, -1));
    setHistorial((h) => [...h, paso]);
    escribir(paso.id, paso.clave, paso.despues);
    toast.success(`Rehecho: ${nombreDe(paso.clave)} de la línea ${paso.orden}`);
  };

  // Los dos atajos, en toda la pantalla y no solo dentro de la celda: se
  // deshace después de haber pinchado en otro sitio tanto como mientras se
  // escribe. Cada uno se intercepta SOLO si tiene algo que hacer — si no, el
  // atajo del navegador sigue valiendo para el buscador y los dos topes, que
  // también son campos de texto.
  useEffect(() => {
    const alTeclear = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      if (e.key !== "z" && e.key !== "Z") return;
      if (e.shiftKey) {
        if (deshecho.length === 0) return;
        e.preventDefault();
        rehacer();
        return;
      }
      if (historial.length === 0) return;
      e.preventDefault();
      deshacer();
    };
    window.addEventListener("keydown", alTeclear);
    return () => window.removeEventListener("keydown", alTeclear);
  });

  const guardar = () => {
    if (!d) return;
    const cambios = Object.entries(tocado).map(([id, campos]) => ({
      id,
      ...campos,
    }));
    if (cambios.length === 0) return;
    editar.mutate(
      { params: { archivoId }, body: { cambios } as never },
      {
        onSuccess: (r: {
          status: number;
          body: {
            dice?: string;
            message?: string;
            bloqueados?: Array<{ orden: number }>;
          };
        }) => {
          if (r.status !== 200) {
            toast.error(r.body.message ?? "No se pudo guardar");
            return;
          }
          toast.success(r.body.dice ?? "Guardado");
          if (r.body.bloqueados?.length) {
            toast.warning(
              `${r.body.bloqueados.length} línea(s) no se tocaron: están conciliadas`,
              {
                description: `Líneas ${r.body.bloqueados
                  .slice(0, 6)
                  .map((b) => b.orden)
                  .join(", ")}. Deshacé su cruce y volvé a intentarlo.`,
                duration: 12_000,
              },
            );
          }
          setTocado({});
          // Los dos historiales también: lo guardado ya no se deshace con
          // Ctrl+Z, se vuelve a corregir. Dejarlos dejaría un atajo que promete
          // revertir algo que ya está escrito en la base.
          setHistorial([]);
          setDeshecho([]);
          qc.invalidateQueries();
        },
        onError: () => toast.error("No se pudo guardar"),
      },
    );
  };

  /**
   * Marca EN TODO EL EXTRACTO lo que cumple un criterio, no en la página.
   *
   * Antes esto recorría `filas`, que son las 200 descargadas, y el aviso decía
   * «solo las de esta página, pasá a la siguiente y volvé a pulsarlo». En un
   * extracto de 3.665 líneas son dieciocho pasadas hechas a mano, y basta
   * olvidar una para cerrar el mes con doscientas salidas contando como
   * pendientes. Quién cumple el criterio lo decide ahora el servidor, que es el
   * único que las ve todas —igual que ya pasaba con el buscador.
   *
   * Se suma a lo ya marcado en vez de reemplazarlo: los dos topes se pueden
   * aplicar uno detrás del otro, y encima se siguen tildando filas a mano.
   *
   * Respeta el filtro puesto: nunca marca lo que quien mira no puede ver.
   */
  const [buscandoIds, setBuscandoIds] = useState(false);
  const marcarEnTodoElExtracto = async (
    criterio: { menoresA?: number; salidasDesde?: number },
    motivo: string,
    siNinguna: string,
  ) => {
    setBuscandoIds(true);
    try {
      // `fetchQuery` y no un hook: esto sale de pulsar un botón, no de pintar.
      // Con `staleTime: 0` cada pulsación vuelve a preguntar, que es lo que hace
      // falta — entre una y otra se pueden haber sacado filas de la conciliación
      // y las que quedan dentro ya no son las mismas.
      const r = await api.idsDelExtracto.fetchQuery(
        qc,
        ["ids", archivoId, JSON.stringify(criterio)],
        {
          params: { archivoId },
          query: {
            buscar: aplicada || undefined,
            conciliado: soloConciliados,
            // Los dos topes SACAN de la conciliación, así que por defecto miran
            // solo las que siguen dentro: volver a marcar una que ya está fuera
            // no cambia nada y engordaría el «N marcadas» con una promesa falsa.
            conciliable: verConciliables ?? "si",
            ...criterio,
          },
        },
        { staleTime: 0 },
      );
      if (r.status !== 200) {
        toast.error("No se pudo buscar en todo el extracto");
        return;
      }
      const { ids, total } = r.body;
      if (ids.length === 0) {
        toast.info(siNinguna);
        return;
      }
      const nuevas = new Set(marcadas);
      for (const id of ids) nuevas.add(id);
      setMarcadas(nuevas);
      setPorQue(motivo);
      toast.success(`${ids.length} línea(s) marcadas en todo el extracto`, {
        description:
          total > ids.length
            ? `Cumplen ${total.toLocaleString("es-PE")}, y ${ids.length.toLocaleString("es-PE")} caben en una tanda. Sacá estas y volvé a pulsarlo.`
            : undefined,
      });
    } catch {
      toast.error("No se pudo buscar en todo el extracto");
    } finally {
      setBuscandoIds(false);
    }
  };

  /**
   * El tope de abajo: lo que no vale la pena conciliar por pequeño.
   *
   * Es el gesto que pidió contabilidad: «pones el valor y te filtra y selecciona
   * esas filas». Mira el ABONO y el CARGO, no el saldo — lo chico es el
   * movimiento, no la cuenta que lo lleva.
   */
  const marcarPorDebajoDelTope = () => {
    const limite = Math.round(Number(tope.replace(",", ".")) * 100);
    if (!Number.isFinite(limite) || limite <= 0) {
      toast.error("Escribí un importe, por ejemplo 10 o 20");
      return;
    }
    void marcarEnTodoElExtracto(
      { menoresA: limite },
      `importes menores a ${tope}`,
      "Ninguna línea del extracto está por debajo de ese importe",
    );
  };

  /**
   * El tope de arriba: las salidas de cierto importe para arriba.
   *
   * Mira SOLO el cargo, y el filo es «de X para arriba» y no «por debajo de X»:
   * lo que estorba aquí es el importe gordo. Con 0 son TODAS las salidas.
   *
   * Desde que las salidas nacen fuera de la conciliación —se marcan solas al
   * cargar el extracto— este botón es sobre todo para lo viejo, lo que ya
   * estaba en la base antes de esa regla.
   */
  const marcarSalidasDesde = () => {
    const limite = Math.round(Number(topeSalida.replace(",", ".")) * 100);
    if (!Number.isFinite(limite) || limite < 0) {
      toast.error("Escribí un importe, por ejemplo 1000", {
        description: "Con 0 se marcan todas las salidas del extracto.",
      });
      return;
    }
    void marcarEnTodoElExtracto(
      { salidasDesde: limite },
      limite === 0 ? "salidas de la cuenta" : `salidas de ${topeSalida} o más`,
      "Ninguna salida del extracto llega a ese importe",
    );
  };

  /**
   * El tilde de la cabecera: todas las del filtro, no las de la página.
   *
   * A diferencia de los dos topes, NO fuerza `conciliable: si`: aquí el filtro
   * puesto manda entero, porque este mismo tilde es el que se usa para devolver
   * a la conciliación un bloque que se sacó de más —y para eso hay que poder
   * seleccionar justamente las que están fuera.
   */
  const marcarTodasLasDelFiltro = async () => {
    setBuscandoIds(true);
    try {
      const r = await api.idsDelExtracto.fetchQuery(
        qc,
        ["ids", archivoId, "todas"],
        {
          params: { archivoId },
          query: {
            buscar: aplicada || undefined,
            conciliado: soloConciliados,
            conciliable: verConciliables,
          },
        },
        { staleTime: 0 },
      );
      if (r.status !== 200) {
        toast.error("No se pudo buscar en todo el extracto");
        return;
      }
      const { ids, total } = r.body;
      setMarcadas(new Set(ids));
      setPorQue(null);
      if (ids.length === 0) {
        toast.info("No hay líneas que marcar");
        return;
      }
      toast.success(`${ids.length} línea(s) marcadas en todo el extracto`, {
        description:
          total > ids.length
            ? `De ${total.toLocaleString("es-PE")}. El resto entra en la siguiente tanda.`
            : undefined,
      });
    } catch {
      toast.error("No se pudo buscar en todo el extracto");
    } finally {
      setBuscandoIds(false);
    }
  };

  const cambiarConciliables = (conciliable: boolean) => {
    if (marcadas.size === 0) return;
    conciliables.mutate(
      {
        params: { archivoId },
        body: {
          ids: [...marcadas],
          conciliable,
          motivo: conciliable ? undefined : (porQue ?? "elegidas a mano"),
        },
      },
      {
        onSuccess: (r: {
          status: number;
          body: {
            dice?: string;
            message?: string;
            bloqueados?: Array<{ orden: number }>;
          };
        }) => {
          if (r.status !== 200) {
            toast.error(r.body.message ?? "No se pudo cambiar");
            return;
          }
          toast.success(r.body.dice ?? "Hecho");
          if (r.body.bloqueados?.length) {
            toast.warning(
              `${r.body.bloqueados.length} no se tocaron: están cruzadas contra una boleta`,
              {
                description: `L\u00edneas ${r.body.bloqueados
                  .slice(0, 6)
                  .map((b) => b.orden)
                  .join(", ")}. Deshac\u00e9 su cruce si de verdad no van.`,
                duration: 12_000,
              },
            );
          }
          setMarcadas(new Set());
          qc.invalidateQueries();
        },
        onError: () => toast.error("No se pudo cambiar"),
      },
    );
  };

  const paginasDeLaTabla = d ? Math.ceil(d.filtradas / POR_PAGINA) : 0;
  const paginas = paginasDeLaTabla;

  // EL SALDO CALCULADO, fila por fila.
  //
  // Pedido así: «una columna que ya la sé calculada, que vaya sumando fila por
  // fila, y yo ver también esa comprobación». Arranca del saldo de apertura que
  // implica la primera línea de LA PÁGINA —su saldo impreso menos lo que ella
  // movió—, así que compara la cadena y no el punto de partida.
  //
  // Solo se calcula sobre lo que se está viendo entero y en orden: con un
  // filtro puesto faltan líneas por medio y la suma no significaría nada. Se
  // dice, en vez de enseñar una columna que miente.
  const enOrden = !aplicada && !soloConciliados;
  const mueve = (m: Movimiento) =>
    (m.abono ?? 0) - (m.cargo ?? 0) - (m.itf ?? 0);
  const calculados: Array<number | null> = [];
  if (enOrden) {
    const primera = filas[0];
    let acumulado =
      primera && primera.saldo !== null ? primera.saldo - mueve(primera) : null;
    for (const m of filas) {
      if (acumulado === null) {
        calculados.push(null);
        continue;
      }
      acumulado += mueve(m);
      calculados.push(acumulado);
    }
  }
  const separadas = new Set<number>();
  filas.forEach((m, i) => {
    const c = calculados[i];
    if (m.saldo !== null && c !== null && c !== undefined && c !== m.saldo)
      separadas.add(i);
  });

  if (isPending) return <Skeleton className="h-96 w-full" />;
  if (!d)
    return (
      <EmptyState
        title="Ese estado de cuenta no existe"
        message="Puede que se haya descartado, o que el enlace sea de una carga anterior."
        action={
          <Button asChild>
            <Link to="/documentos/estados-de-cuenta">
              <ArrowLeft className="mr-2 size-4" />
              Volver al checklist
            </Link>
          </Button>
        }
      />
    );

  const bloqueadoTodo = d.periodoCerrado;

  return (
    <>
      {/* La cabecera dice de quién es el extracto antes que nada: una tabla de
          movimientos sin su razón social son ciento y pico de cifras sueltas. */}
      <div className="flex flex-wrap items-start gap-x-4 gap-y-2">
        <Button size="sm" variant="ghost" asChild>
          <Link to="/documentos/estados-de-cuenta">
            <ArrowLeft className="mr-1.5 size-4" />
            Estados de cuenta
          </Link>
        </Button>
        <div className="min-w-0">
          <h1 className="font-semibold text-[19px] tracking-tight">
            {d.banco ?? "Banco sin detectar"}{" "}
            <span className="tabular-nums">{d.cuenta ?? ""}</span>{" "}
            <span className="text-[15px] text-muted-foreground">
              {d.moneda ? SIMBOLO[d.moneda as "PEN" | "USD"] : ""}
            </span>
          </h1>
          <p className="text-[12.5px] text-muted-foreground">
            {d.empresa ?? "sin empresa resuelta"}
            {d.ruc ? ` · RUC ${d.ruc}` : ""}
            {d.desde && d.hasta ? ` · del ${d.desde} al ${d.hasta}` : ""}
          </p>
        </div>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          <BotonDelPapel
            abierto={conPapel}
            alPulsar={() => setConPapel((x) => !x)}
          />
          {/* Borrar la carga. Solo de jefe para arriba, y el permiso lo dice el
              servidor: la pantalla no tiene por qué saber la escalera de roles.
              A quien no lo tiene no se le enseña un botón que le va a decir
              que no. */}
          {d.puedeEliminar && !bloqueadoTodo ? (
            <Button
              size="sm"
              variant="outline"
              disabled={eliminar.isPending}
              className="border-red-600/40 text-red-700 hover:bg-red-500/10 dark:text-red-400"
              onClick={() => {
                const motivo = window.prompt(
                  `Se van a borrar «${d.nombre.replace(/^.*\//, "")}» y sus ` +
                    `${d.total.toLocaleString("es-PE")} movimientos.\n\n` +
                    "Esto no se deshace, y queda anotado en la auditoría con tu " +
                    "nombre.\n\n¿Por qué se borra?",
                  "El banco entregó un PDF equivocado",
                );
                if (!motivo || motivo.trim().length < 3) return;
                eliminar.mutate(
                  {
                    params: { id: archivoId },
                    body: { confirmo: true, motivo: motivo.trim() },
                  },
                  {
                    onSuccess: (r: {
                      status: number;
                      body: { dice?: string; message?: string };
                    }) => {
                      if (r.status !== 200) {
                        toast.error(r.body.message ?? "No se pudo borrar", {
                          duration: 12_000,
                        });
                        return;
                      }
                      toast.success(r.body.dice ?? "Borrado", {
                        description:
                          "Subí el PDF nuevo desde «Cargar el mes» y confirmalo.",
                        duration: 10_000,
                      });
                      qc.invalidateQueries();
                      navegar({ to: "/carga" });
                    },
                    onError: () => toast.error("No se pudo borrar"),
                  },
                );
              }}
            >
              <Trash2 className="mr-1.5 size-3.5" />
              {eliminar.isPending ? "Borrando…" : "Eliminar y volver a cargar"}
            </Button>
          ) : null}
        </div>
      </div>

      {/* El cuadre, que es lo que dice si el extracto se leyó entero. */}
      {d.cuadre ? (
        <div
          className={cn(
            "rounded-lg border px-4 py-3 text-[12.5px]",
            d.cuadre.cuadra
              ? "border-emerald-600/30 bg-emerald-600/5"
              : "border-red-600/40 bg-red-600/5",
          )}
        >
          <div className="flex flex-wrap gap-x-6 gap-y-1 tabular-nums">
            <span>
              <span className="text-muted-foreground">Entró </span>
              <b>{dinero(d.cuadre.calculadoAbonos)}</b>
              {d.cuadre.declaradoAbonos !== null ? (
                <span className="text-muted-foreground">
                  {" "}
                  · el banco declara {dinero(d.cuadre.declaradoAbonos)}
                </span>
              ) : null}
            </span>
            <span>
              <span className="text-muted-foreground">Salió </span>
              <b>{dinero(d.cuadre.calculadoCargos)}</b>
              {d.cuadre.declaradoCargos !== null ? (
                <span className="text-muted-foreground">
                  {" "}
                  · el banco declara {dinero(d.cuadre.declaradoCargos)}
                </span>
              ) : null}
            </span>
            <span className="text-muted-foreground">
              {d.total.toLocaleString("es-PE")} movimientos ·{" "}
              {d.conciliados.toLocaleString("es-PE")} conciliados
              {/* Las apartadas van en la misma tira y no escondidas en la
                  barra de abajo: quien lee «80 movimientos, 5 conciliados» sin
                  esto entiende que quedan 75 por decidir, y si hay 12 fuera
                  quedan 63. La cuenta cambia y la decisión también. */}
              {d.noConciliables > 0 ? (
                <>
                  {" · "}
                  <b className="text-foreground">
                    {d.noConciliables.toLocaleString("es-PE")}
                  </b>{" "}
                  fuera de la conciliación
                </>
              ) : null}
            </span>
          </div>
          {d.hallazgos.length > 0 ? (
            <ul className="mt-1.5 space-y-0.5 text-[11.5px] text-muted-foreground">
              {(
                d.hallazgos as Array<{
                  codigo: string;
                  gravedad: string;
                  mensaje: string;
                }>
              ).map((h) => (
                <li key={h.codigo + h.mensaje}>
                  <b>{h.gravedad === "error" ? "Error" : "Aviso"}:</b>{" "}
                  {h.mensaje}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      {/* Buscar y filtrar. Va contra el servidor y no sobre lo descargado:
          buscar dentro de la página que se está viendo encuentra en 200 líneas
          de 1.500 y no dice que el resto no se miró. */}
      <div className="flex flex-wrap items-center gap-2">
        <form
          className="flex min-w-[260px] flex-1 items-center gap-2 rounded-md border px-2.5"
          onSubmit={(e) => {
            e.preventDefault();
            setPagina(0);
            setAplicada(busca.trim());
          }}
        >
          <Search className="size-3.5 shrink-0 text-muted-foreground" />
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar en descripción, referencia o n.º de operación"
            className="w-full bg-transparent py-1.5 text-[12.5px] focus:outline-none"
          />
          {aplicada ? (
            <button
              type="button"
              className="text-[11.5px] text-muted-foreground hover:text-foreground"
              onClick={() => {
                setBusca("");
                setAplicada("");
                setPagina(0);
              }}
            >
              limpiar
            </button>
          ) : null}
        </form>

        {/* EL FILTRO POR IMPORTE.
            Tres piezas en el orden en que se piensa: qué columna, cómo se
            compara y contra cuánto. El monto va en unidades enteras y el
            servidor redondea, así que «300» encuentra también el de 299.99. */}
        <form
          className="flex items-center gap-1 rounded-md border p-0.5"
          onSubmit={(e) => {
            e.preventDefault();
            const n = Number.parseInt(monto.replace(/[^\d-]/g, ""), 10);
            setMontoAplicado(Number.isFinite(n) ? n : undefined);
            setPagina(0);
          }}
        >
          <select
            aria-label="Qué importe se compara"
            value={campo}
            onChange={(e) => {
              setCampo(e.target.value as typeof campo);
              setPagina(0);
            }}
            className="rounded bg-transparent px-1.5 py-1 text-[12px] focus:outline-none"
          >
            <option value="abono">Entró</option>
            <option value="cargo">Salió</option>
            <option value="saldo">Saldo</option>
            <option value="itf">ITF</option>
          </select>
          <select
            aria-label="Cómo se compara el importe"
            value={comparar}
            onChange={(e) => {
              setComparar(e.target.value as typeof comparar);
              setPagina(0);
            }}
            className="rounded bg-transparent px-1 py-1 text-[12px] focus:outline-none"
          >
            <option value="igual">igual a</option>
            <option value="mayor">mayor que</option>
            <option value="menor">menor que</option>
          </select>
          <input
            value={monto}
            onChange={(e) => setMonto(e.target.value)}
            inputMode="numeric"
            placeholder="monto"
            aria-label="Importe contra el que comparar"
            title="En unidades enteras. Se redondea: 300 encuentra también 299.99"
            className="w-[86px] rounded border bg-background px-2 py-1 text-right text-[12px] tabular-nums focus:border-cyan-500 focus:outline-none"
          />
          <Button size="sm" variant="outline" type="submit">
            Filtrar
          </Button>
          {montoAplicado !== undefined ? (
            <button
              type="button"
              className="px-1 text-[11.5px] text-muted-foreground hover:text-foreground"
              onClick={() => {
                setMonto("");
                setMontoAplicado(undefined);
                setPagina(0);
              }}
            >
              limpiar
            </button>
          ) : null}
        </form>

        <div className="flex rounded-md border p-0.5">
          {(
            [
              [undefined, "Todos"],
              ["no", "Sin conciliar"],
              ["si", "Conciliados"],
            ] as const
          ).map(([v, texto]) => (
            <button
              key={texto}
              type="button"
              onClick={() => {
                setSoloConciliados(v);
                setPagina(0);
              }}
              className={cn(
                "rounded px-2.5 py-1 font-medium text-[12px] transition-colors",
                soloConciliados === v
                  ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {texto}
            </button>
          ))}
        </div>

        <div className="ml-auto flex items-center gap-2">
          {sinGuardar > 0 ? (
            <span className="text-[11.5px] text-amber-700 dark:text-amber-500">
              {sinGuardar} sin guardar
            </span>
          ) : null}
          <Button
            size="sm"
            disabled={sinGuardar === 0 || editar.isPending || bloqueadoTodo}
            onClick={guardar}
          >
            {editar.isPending ? "Guardando…" : "Guardar los cambios"}
          </Button>
        </div>
      </div>

      {/* -------------------------------------------------------------------
          LO QUE NO SE CONCILIA.
          Un extracto trae líneas que nunca van a cruzar contra una boleta —el
          ITF de dos soles, la comisión, el abono de céntimos de una pasarela— y
          mientras cuenten como pendientes el mes no se puede cerrar y la cola de
          revisión se llena de casos sin solución. Se marcan, no se borran: la
          línea sigue sumando en el cuadre y en la cadena de saldos.
      ------------------------------------------------------------------- */}
      {!bloqueadoTodo ? (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/30 px-3 py-2 text-[12.5px]">
          <span className="font-semibold text-[10.5px] text-muted-foreground uppercase tracking-wider">
            Fuera de la conciliación
          </span>

          <span className="flex items-center gap-1.5">
            <span className="text-muted-foreground">Marcar las menores a</span>
            <span className="text-muted-foreground">
              {d.moneda ? SIMBOLO[d.moneda as "PEN" | "USD"] : ""}
            </span>
            <input
              value={tope}
              onChange={(e) => setTope(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") marcarPorDebajoDelTope();
              }}
              inputMode="decimal"
              aria-label="Importe por debajo del cual marcar"
              className="w-[74px] rounded border bg-background px-2 py-1 text-right tabular-nums focus:border-cyan-500 focus:outline-none"
            />
            <Button
              size="sm"
              variant="outline"
              disabled={buscandoIds}
              onClick={marcarPorDebajoDelTope}
            >
              Marcar
            </Button>
          </span>

          {/* El otro extremo: las salidas grandes. Mismo gesto, filo al revés
              —«de X para arriba»— porque lo que estorba aquí es el importe
              gordo, no el chico. */}
          <span className="flex items-center gap-1.5 border-l pl-2">
            <span className="text-muted-foreground">Marcar las salidas de</span>
            <span className="text-muted-foreground">
              {d.moneda ? SIMBOLO[d.moneda as "PEN" | "USD"] : ""}
            </span>
            <input
              value={topeSalida}
              onChange={(e) => setTopeSalida(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") marcarSalidasDesde();
              }}
              inputMode="decimal"
              aria-label="Importe a partir del cual marcar las salidas"
              title="Con 0 se marcan todas las salidas del extracto"
              className="w-[84px] rounded border bg-background px-2 py-1 text-right tabular-nums focus:border-cyan-500 focus:outline-none"
            />
            <span className="text-muted-foreground">para arriba</span>
            <Button
              size="sm"
              variant="outline"
              disabled={buscandoIds}
              onClick={marcarSalidasDesde}
            >
              Marcar
            </Button>
          </span>

          {marcadas.size > 0 ? (
            <>
              <span className="text-muted-foreground">
                <b className="text-foreground tabular-nums">{marcadas.size}</b>{" "}
                marcada{marcadas.size === 1 ? "" : "s"}
              </span>
              <Button
                size="sm"
                variant="outline"
                disabled={conciliables.isPending}
                onClick={() => cambiarConciliables(false)}
              >
                <EyeOff className="mr-1.5 size-3.5" />
                No conciliar estas
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={conciliables.isPending}
                onClick={() => cambiarConciliables(true)}
              >
                Devolver a la conciliación
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setMarcadas(new Set())}
              >
                Quitar la marca
              </Button>
            </>
          ) : (
            <span className="text-muted-foreground">
              o tildá las filas una a una
            </span>
          )}

          {/* Cuántas hay ya fuera, y cómo verlas: una decisión que no se puede
              revisar es una decisión que nadie se atreve a tomar. */}
          {d.noConciliables > 0 ? (
            <button
              type="button"
              onClick={() => {
                setVerConciliables(verConciliables === "no" ? undefined : "no");
                setPagina(0);
              }}
              className={cn(
                "ml-auto rounded border px-2 py-0.5 text-[11.5px] transition-colors",
                verConciliables === "no"
                  ? "border-cyan-500 bg-cyan-500/10 text-cyan-700 dark:text-cyan-400"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {d.noConciliables} fuera de la conciliación
              {verConciliables === "no" ? " · viendo solo estas" : " · ver"}
            </button>
          ) : null}
        </div>
      ) : null}

      {bloqueadoTodo ? (
        <p className="flex items-center gap-2 rounded border border-amber-600/40 bg-amber-500/10 px-3 py-2 text-[12.5px] text-amber-700 dark:text-amber-500">
          <Lock className="size-3.5 shrink-0" />
          El periodo {d.periodo} está cerrado: sus movimientos ya no se editan.
        </p>
      ) : (
        <p className="text-[11.5px] text-muted-foreground">
          Se escribe directo en la celda.{" "}
          {historial.length > 0 ? (
            <>
              <Tecla>Ctrl</Tecla>+<Tecla>Z</Tecla> deshace la última casilla.{" "}
            </>
          ) : null}
          {deshecho.length > 0 ? (
            <>
              <Tecla>Ctrl</Tecla>+<Tecla>Shift</Tecla>+<Tecla>Z</Tecla> la
              rehace.{" "}
            </>
          ) : null}
          Las líneas con candado están cruzadas contra una boleta: para
          corregirlas hay que deshacer su cruce primero.
        </p>
      )}

      {/* -------------------------------------------------------------------
          LA TABLA Y EL PAPEL, CADA UNO CON SU SCROLL.
          La zona ocupa lo que queda de ventana y cada lado se desplaza por
          dentro: bajando por la tabla el papel se queda donde estaba, que es
          para lo que sirve tenerlo al lado. El divisor del medio reparte el
          ancho y se recuerda.
      ------------------------------------------------------------------- */}
      <DosPaneles
        clave="ancho-ficha-extracto"
        soloIzquierda={!conPapel}
        derecha={
          <PapelDelExtracto
            archivoId={archivoId}
            paginas={d.paginas}
            alAgrandar={(pagina) =>
              setPapel({
                clase: "expediente",
                archivoId,
                pagina,
                paginas: d.paginas,
                titulo: `${d.banco ?? ""} ${d.cuenta ?? ""}`.trim(),
              })
            }
          />
        }
        izquierda={
          <>
            {d.movimientos.length === 0 ? (
              <p className="shrink-0 py-10 text-center text-muted-foreground text-sm">
                {aplicada || soloConciliados
                  ? "Ningún movimiento coincide con ese filtro."
                  : "Este extracto no trajo ningún movimiento."}
              </p>
            ) : (
              <div className="min-h-0 flex-1 overflow-auto rounded-lg border">
                {/* `min-w` y no solo `w-full`: con el PDF al lado la columna se
                  estrecha, y sin un ancho mínimo la descripción —que es lo que
                  se lee para reconocer el movimiento— se corta en «TRAN.CTAS».
                  Con esto la tabla se desplaza a lo ancho dentro de su caja y
                  la descripción sigue entera. */}
                <table className="w-full min-w-[1180px] text-[12.5px]">
                  <thead className="sticky top-0 z-10 bg-background">
                    {/* LA CABECERA TIENE QUE LLEVAR UNA CELDA POR COLUMNA DEL
                        CUERPO, y las dos condicionales van con la MISMA
                        condición que su celda. Cuando no era así, la tabla se
                        leía corrida: bajo «Descripción» salía la fecha y bajo
                        «N.º operación» la descripción. */}
                    <tr className="border-b text-[10.5px] text-muted-foreground uppercase tracking-wider">
                      {!bloqueadoTodo ? (
                        <th className="w-[34px] px-2 py-1.5">
                          {/* Marca TODO el extracto, no la página. Destildar
                              limpia la selección entera por lo mismo: quitar
                              solo las de la página dejaba marcadas las de las
                              otras sin decirlo, y el botón seguía ofreciendo
                              sacar de la conciliación un bloque invisible. */}
                          <input
                            type="checkbox"
                            aria-label="Marcar todas las del extracto"
                            title="Marca todas las del extracto, no solo las de esta página"
                            disabled={buscandoIds}
                            className="size-3.5 align-middle accent-cyan-600"
                            checked={
                              filas.length > 0 &&
                              filas.every(
                                (m) => m.conciliado || marcadas.has(m.id),
                              )
                            }
                            onChange={(e) => {
                              if (!e.target.checked) {
                                setMarcadas(new Set());
                                setPorQue(null);
                                return;
                              }
                              void marcarTodasLasDelFiltro();
                            }}
                          />
                        </th>
                      ) : null}
                      <th className="w-[52px] px-2 py-1.5 text-right font-semibold">
                        #
                      </th>
                      {COLUMNAS.map((c) => (
                        <th
                          key={c.clave}
                          className={cn(
                            "px-1 py-1.5 font-semibold",
                            c.ancho,
                            "dinero" in c && c.dinero
                              ? "text-right"
                              : "text-left",
                          )}
                        >
                          {c.titulo}
                        </th>
                      ))}
                      {/* No se escribe: sale de sumar las líneas. Está para
                          comprobarla contra la que imprime el banco. */}
                      <th
                        className="w-[124px] px-1 py-1.5 text-right font-semibold"
                        title="Saldo que sale de sumar las líneas, una a una"
                      >
                        Saldo calc.
                      </th>
                      <th className="w-[120px] px-2 py-1.5 text-left font-semibold">
                        Estado
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filas.map((m, i) => (
                      <Fila
                        key={m.id}
                        m={m}
                        tocada={Boolean(tocado[m.id])}
                        bloqueada={bloqueadoTodo}
                        calculado={enOrden ? (calculados[i] ?? null) : null}
                        seSepara={separadas.has(i)}
                        marcada={marcadas.has(m.id)}
                        conCasilla={!bloqueadoTodo}
                        alMarcar={(marcar) => {
                          setMarcadas((antes) => {
                            const nuevas = new Set(antes);
                            if (marcar) nuevas.add(m.id);
                            else nuevas.delete(m.id);
                            return nuevas;
                          });
                          setPorQue(null);
                        }}
                        alCambiar={cambiar}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {paginas > 1 ? (
              <div className="flex shrink-0 items-center justify-between pt-3 text-[12.5px]">
                <span className="text-muted-foreground tabular-nums">
                  {(pagina * POR_PAGINA + 1).toLocaleString("es-PE")}–
                  {Math.min(
                    (pagina + 1) * POR_PAGINA,
                    d.filtradas,
                  ).toLocaleString("es-PE")}{" "}
                  de {d.filtradas.toLocaleString("es-PE")}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={pagina === 0 || sinGuardar > 0}
                    onClick={() => setPagina((p) => Math.max(0, p - 1))}
                  >
                    <ChevronLeft className="size-3.5" />
                  </Button>
                  <span className="tabular-nums">
                    {pagina + 1} / {paginas}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={pagina + 1 >= paginas || sinGuardar > 0}
                    onClick={() => setPagina((p) => p + 1)}
                  >
                    <ChevronRight className="size-3.5" />
                  </Button>
                </div>
              </div>
            ) : null}

            {/* Cambiar de página con cosas escritas las perdería: lo escrito vive en
          esta pantalla, no en el servidor, y la página siguiente trae otras
          filas. Se dice, y los botones quedan quietos hasta guardar. */}
            {sinGuardar > 0 && paginas > 1 ? (
              <p className="shrink-0 pt-1.5 text-[11.5px] text-amber-700 dark:text-amber-500">
                Guardá los cambios antes de cambiar de página.
              </p>
            ) : null}
          </>
        }
      />

      {/* El visor a pantalla completa, para leer un número de operación que en
          la columna de al lado no se distingue. */}
      {papel ? <Visor papel={papel} alCerrar={() => setPapel(null)} /> : null}
    </>
  );
}

function Fila({
  m,
  tocada,
  bloqueada,
  calculado,
  seSepara,
  marcada,
  conCasilla,
  alMarcar,
  alCambiar,
}: {
  m: Movimiento;
  tocada: boolean;
  bloqueada: boolean;
  /** El saldo que sale de sumar. `null` cuando hay un filtro puesto. */
  calculado: number | null;
  /** El calculado no coincide con el que imprime el banco. */
  seSepara: boolean;
  /** Marcada para actuar sobre ella en bloque. */
  marcada: boolean;
  /** El periodo está abierto: se puede marcar. */
  conCasilla: boolean;
  alMarcar: (marcar: boolean) => void;
  alCambiar: (m: Movimiento, clave: ClaveColumna, valor: string) => void;
}) {
  const noSeToca = m.conciliado || bloqueada;

  return (
    <tr
      className={cn(
        "border-b last:border-0",
        tocada && "bg-amber-500/5",
        m.conciliado && "bg-muted/30",
        marcada && "bg-cyan-500/10",
        // Fuera de la conciliación: se ve que sigue ahí y que no cuenta.
        !m.conciliable && "bg-muted/40 opacity-60",
      )}
    >
      {conCasilla ? (
        <td className="px-2 py-0.5">
          <input
            type="checkbox"
            aria-label={`Marcar la línea ${m.orden}`}
            checked={marcada}
            disabled={m.conciliado}
            onChange={(e) => alMarcar(e.target.checked)}
            className="size-3.5 align-middle accent-cyan-600 disabled:opacity-40"
          />
        </td>
      ) : null}
      <td className="px-2 py-0.5 text-right text-[11px] text-muted-foreground tabular-nums">
        {m.orden}
      </td>
      {COLUMNAS.map((c) => {
        const esDinero = "dinero" in c && c.dinero;
        const valor = esDinero
          ? aTexto(m[c.clave] as number | null)
          : ((m[c.clave] as string | null) ?? "");
        if (noSeToca) {
          return (
            <td
              key={c.clave}
              className={cn(
                "px-2.5 py-1.5 text-muted-foreground",
                esDinero && "text-right tabular-nums",
              )}
            >
              {valor || "—"}
            </td>
          );
        }
        return (
          <td key={c.clave} className="px-0.5 py-0.5">
            <Celda
              m={m}
              clave={c.clave}
              esDinero={Boolean(esDinero)}
              alCambiar={alCambiar}
            />
          </td>
        );
      })}
      {/* El calculado, y en rojo cuando se separa del impreso: ahí es donde el
          lector se saltó algo, o donde una corrección todavía no cierra. */}
      <td
        className={cn(
          "px-2 py-1.5 text-right tabular-nums",
          seSepara
            ? "font-semibold text-red-700 dark:text-red-500"
            : "text-muted-foreground",
        )}
      >
        {calculado === null ? "—" : dinero(calculado)}
      </td>
      <td className="px-2 py-1.5">
        {m.conciliado ? (
          <span className="flex items-center gap-1.5 text-[11.5px] text-muted-foreground">
            <Lock className="size-3 shrink-0" />
            Conciliado
          </span>
        ) : !m.conciliable ? (
          <span className="flex items-center gap-1.5 text-[11.5px] text-muted-foreground">
            <EyeOff className="size-3 shrink-0" />
            No se concilia
          </span>
        ) : (
          <span className="text-[11.5px] text-muted-foreground">
            sin conciliar
          </span>
        )}
      </td>
    </tr>
  );
}

/**
 * Una celda editable de la rejilla.
 *
 * EXISTE POR UN FALLO QUE HACÍA LA TABLA CASI INUSABLE. El input estaba
 * controlado directamente contra el modelo: cada tecla se convertía a céntimos
 * y volvía formateada a la pantalla. Al cambiar 7500 por 4500, la primera tecla
 * —«4»— se reescribía sola como «4.00», el cursor saltaba al final, y la
 * siguiente caía detrás de los decimales. Se podía escribir, pero no salía lo
 * que se escribía.
 *
 * La celda guarda ahora SU PROPIO borrador mientras se teclea, y el modelo se
 * actualiza en paralelo —el saldo calculado tiene que moverse con lo escrito,
 * que es la comprobación por la que existe esa columna.
 *
 * Y se vuelve a sincronizar cuando el valor cambia POR FUERA: al deshacer con
 * Ctrl+Z, al guardar, al recargar. Distinguir un caso del otro es comparar lo
 * que se tiene tecleado contra el modelo: si coinciden, el cambio lo hicimos
 * nosotros al escribir y el borrador manda; si no, vino de fuera y manda él.
 */
function Celda({
  m,
  clave,
  esDinero,
  alCambiar,
}: {
  m: Movimiento;
  clave: ClaveColumna;
  esDinero: boolean;
  alCambiar: (m: Movimiento, clave: ClaveColumna, valor: string) => void;
}) {
  const bruto = m[clave] as string | number | null;

  const leer = (v: string | number | null) =>
    esDinero ? aTexto(v as number | null) : ((v as string | null) ?? "");
  const interpretar = (t: string): string | number | null =>
    esDinero ? aCentimos(t) : t === "" ? null : t;

  const [texto, setTexto] = useState(() => leer(bruto));
  const [visto, setVisto] = useState<string | number | null>(bruto);

  // Ajuste de estado durante el render, que es la forma recomendada de
  // reaccionar a un cambio de props sin un efecto que pinte dos veces.
  if (bruto !== visto) {
    if (bruto !== interpretar(texto)) setTexto(leer(bruto));
    setVisto(bruto);
  }

  return (
    <input
      value={texto}
      inputMode={esDinero ? "decimal" : undefined}
      onChange={(e) => {
        setTexto(e.target.value);
        alCambiar(m, clave, e.target.value);
      }}
      // Al entrar, la cifra se queda sin separadores: es más cómoda de
      // corregir. Al salir, vuelve a leerse como el resto de la tabla.
      onFocus={() => {
        if (esDinero) setTexto(aTextoLlano(bruto as number | null));
      }}
      onBlur={() => setTexto(leer(bruto))}
      className={cn(
        "w-full rounded-sm border border-transparent bg-transparent px-1.5 py-1 focus:border-cyan-500 focus:bg-background focus:outline-none",
        esDinero && "text-right tabular-nums",
      )}
    />
  );
}
